import { EventType } from "@/analytics/analytics-events";
import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { worldVecToScreenVec } from "@/registration-tools/common/interaction/single-pin-interaction";
import { useThresholdSetContext } from "@/registration-tools/common/registration-report/threshold-set-context";
import {
  QualityStatus,
  RegistrationDetails,
} from "@/registration-tools/utils/metrics";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { useThreeEventTargetRef } from "@faro-lotv/app-component-toolbox";
import { FaroPopover } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { MapPlaceholders } from "@faro-lotv/lotv";
import {
  RegistrationEdgeRevision,
  RegistrationState,
} from "@faro-lotv/service-wires";
import { useCursor } from "@react-three/drei";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { Plane, Vector2, Vector3 } from "three";
import {
  selectHoveredConnectionId,
  selectSelectedConnectionDetails,
} from "../store/data-preparation-ui/data-preparation-ui-selectors";
import {
  setHoveredConnectionId,
  setSelectedConnectionId,
  unsetSelectedConnectionId,
} from "../store/data-preparation-ui/data-preparation-ui-slice";
import { setShowConnectionLines } from "../store/data-preparation-view-options/data-preparation-view-options-slice";
import { selectRevisionEntityScan } from "../store/revision/revision-scan-selectors";
import {
  selectActivePartOfEdges,
  selectActiveRevisionEdges,
  selectActiveRevisionHoveredEdges,
} from "../store/select-active-revision-edges";
import {
  selectIsAddingConnections,
  selectIsAnyEditModeEnabled,
  selectIsDeletingConnections,
} from "../store/user-edits/data-preparation-user-edit-selectors";
import { addUserEditDeleteConnection } from "../store/user-edits/data-preparation-user-edit-slice";
import { ConnectionQualityDialog } from "../ui/data-preparation-dialogs/data-preparation-connection-quality-dialog";
import {
  QualityPopover,
  QualityScope,
  VirtualElement,
} from "../ui/quality-popover";
import { useScanPosition } from "../utils/entity-utils";
import {
  ConnectionScanNames,
  DrawUserAddedConnection,
  TOOLTIP_Z_INDEX,
} from "./registration-connections";
import { RegistrationEdgeRenderer } from "./registration-edge-renderer";

/** The base size for connection lines. */
const LINE_WIDTH = 5;
/** Size factor for PartOf connections. */
const PART_OF_FACTOR = 0.5;

type RegistrationConnectionsBatchProps = {
  /** The current state of the revision. */
  revisionState?: RegistrationState;
  /** Clipping planes to clip the connection lines with. */
  clippingPlanes?: Plane[];
};

/**
 * @returns Connection lines representing registration of the connected captures.
 * Using instanced rendering for better performance.
 */
export function RegistrationConnectionsInstanced({
  revisionState,
  clippingPlanes,
}: RegistrationConnectionsBatchProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const portal = useThreeEventTargetRef();

  useAutoEnableConnectionLines();

  const isAddingConnections = useAppSelector(selectIsAddingConnections);
  const isDeletingConnections = useAppSelector(selectIsDeletingConnections);
  const hasDevFeature = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );

  const activeEdges = useAppSelector(selectActiveRevisionEdges);
  const activePartOfEdges = useAppSelector(selectActivePartOfEdges);

  const [directHoverPosition, setDirectHoverPosition] = useState<
    Vector2 | undefined
  >();
  const hoveredConnectionId = useAppSelector(selectHoveredConnectionId);
  const hoveredEdges = useAppSelector(selectActiveRevisionHoveredEdges);
  const selectedEdge = useAppSelector(selectSelectedConnectionDetails);

  const highlightedEdgeIds = useMemo(() => {
    const ids = hoveredEdges.map((edge) => edge.id);

    if (selectedEdge) {
      ids.push(selectedEdge.id);
    }

    return ids;
  }, [hoveredEdges, selectedEdge]);

  // Get source and target entities for the hovered edge at the top level (required for hooks)
  const hoveredEdgeSourceEntity = useAppSelector(
    selectRevisionEntityScan(hoveredEdges[0]?.sourceId),
  );
  const hoveredEdgeTargetEntity = useAppSelector(
    selectRevisionEntityScan(hoveredEdges[0]?.targetId),
  );

  // Get scan positions using hooks at the top level
  const hoveredSourcePosition = useScanPosition(hoveredEdgeSourceEntity);
  const hoveredTargetPosition = useScanPosition(hoveredEdgeTargetEntity);

  const [clickPosition, setClickPosition] = useState<Vector2>(new Vector2());
  const camera = useThree((state) => state.camera);
  const canvasSize = useThree((state) => state.size);
  const boundingRect = useThree((state) =>
    state.gl.domElement.getBoundingClientRect(),
  );

  // Pointer cursor when deleting connections and hovering over a connection line.
  useCursor(isDeletingConnections && hoveredConnectionId !== undefined);

  // Calculate the hover position. If the user is directly hovering over an edge, use the pointer position.
  // Otherwise, calculate the mid point of the connection
  const hoverPosition = useMemo(() => {
    if (directHoverPosition) {
      return directHoverPosition;
    }

    if (hoveredEdges[0] && hoveredEdgeSourceEntity && hoveredEdgeTargetEntity) {
      const connectionMidpointWorld = new Vector3(
        (hoveredSourcePosition[0] + hoveredTargetPosition[0]) / 2,
        (hoveredSourcePosition[1] + hoveredTargetPosition[1]) / 2,
        (hoveredSourcePosition[2] + hoveredTargetPosition[2]) / 2,
      );

      const connectionMidpointScreen = worldVecToScreenVec(
        connectionMidpointWorld,
        new Vector3(),
        camera,
        canvasSize.width,
        canvasSize.height,
      );

      return new Vector2(
        connectionMidpointScreen.x + boundingRect.left,
        connectionMidpointScreen.y + boundingRect.top,
      );
    }
    return new Vector2();
  }, [
    hoveredEdges,
    hoveredEdgeSourceEntity,
    hoveredEdgeTargetEntity,
    hoveredSourcePosition,
    hoveredTargetPosition,
    directHoverPosition,
    camera,
    canvasSize,
    boundingRect,
  ]);

  const onPointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (
        event.index === undefined ||
        eventAffectsMarkers(event) ||
        isAddingConnections
      ) {
        setDirectHoverPosition(undefined);
        if (hoveredConnectionId !== undefined) {
          dispatch(setHoveredConnectionId(undefined));
        }
        return;
      }

      // Update position where connection is being hovered
      setDirectHoverPosition(new Vector2(event.clientX, event.clientY));

      // Update _which_ line is being hovered
      if (
        event.intersections.some(
          (intersection) =>
            intersection.index !== undefined &&
            activeEdges[intersection.index].id === hoveredConnectionId,
        )
      ) {
        // Prevent flickering by preferring the line that is already hovered
        return;
      }

      const connectionIdx = event.index;
      const edgeId = activeEdges[connectionIdx].id;
      dispatch(setHoveredConnectionId(edgeId));
    },
    [activeEdges, dispatch, isAddingConnections, hoveredConnectionId],
  );
  const onConnectionClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (event.index === undefined || eventAffectsMarkers(event)) return;

      // Disable click effects on edges when adding a new connection
      if (isAddingConnections) return;

      const connectionIdx = event.index;
      const edge = activeEdges[connectionIdx];

      const state = getState();
      const currentUser = selectCurrentUser(state);

      if (hasDevFeature) {
        console.debug("Clicked connection", edge);
      }

      const { id, sourceId, targetId } = edge;

      if (selectIsDeletingConnections(state) && currentUser) {
        // Delete the connection
        Analytics.track(EventType.deleteRegistrationConnection);
        dispatch(
          addUserEditDeleteConnection({
            sourceId,
            targetId,
            createdBy: currentUser.id,
          }),
        );
        // Ensure that the deleted connection is not selected or hovered anymore
        dispatch(setSelectedConnectionId(undefined));
        dispatch(setHoveredConnectionId(undefined));
        return;
      }

      dispatch(setSelectedConnectionId(id));
      setClickPosition(new Vector2(event.clientX, event.clientY));
    },
    [dispatch, getState, activeEdges, isAddingConnections, hasDevFeature],
  );

  const onHoverEnd = useCallback(() => {
    setDirectHoverPosition(undefined);
    dispatch(setHoveredConnectionId(undefined));
  }, [dispatch]);

  return (
    <group name="registration connections">
      {/* Render active connections */}
      <RegistrationEdgeRenderer
        name="active connections"
        registrationEdges={activeEdges}
        clippingPlanes={clippingPlanes}
        renderOrder={0}
        onPointerMove={onPointerMove}
        onHoverEnd={onHoverEnd}
        onClick={onConnectionClick}
        highlightedEdgeIds={highlightedEdgeIds}
      />

      {/* Render PartOf edges with a thinner line */}
      <RegistrationEdgeRenderer
        name="PartOf connections"
        renderOrder={1}
        registrationEdges={activePartOfEdges}
        clippingPlanes={clippingPlanes}
        linewidth={LINE_WIDTH * PART_OF_FACTOR}
        highlightedEdgeIds={highlightedEdgeIds}
      />

      {/** Connection drawn by the user while adding a new connection */}
      <DrawUserAddedConnection />

      <AppAwareHtml
        portal={portal}
        zIndexRange={[TOOLTIP_Z_INDEX, TOOLTIP_Z_INDEX]}
      >
        <RegistrationConnectionPopovers
          revisionState={revisionState}
          hoveredEdge={hoveredEdges[0]}
          hoverPosition={hoverPosition}
          clickedEdge={selectedEdge}
          clickPosition={clickPosition}
          unsetClickInfo={() => {
            dispatch(unsetSelectedConnectionId(selectedEdge?.id));
          }}
        />
      </AppAwareHtml>
    </group>
  );
}

/**
 * Automatically enable connection lines when edit mode is enabled and disable them when not.
 */
function useAutoEnableConnectionLines(): void {
  const dispatch = useAppDispatch();
  const isEditModeEnabled = useAppSelector(selectIsAnyEditModeEnabled);

  useEffect(() => {
    dispatch(setShowConnectionLines(isEditModeEnabled));
  }, [dispatch, isEditModeEnabled]);
}

type RegistrationConnectionPopoversProps = {
  /** The registration state of the scans, used to display connections between scans. */
  revisionState?: RegistrationState;
  hoveredEdge?: RegistrationEdgeRevision;
  hoverPosition: Vector2;
  clickedEdge?: RegistrationEdgeRevision;
  clickPosition: Vector2;
  unsetClickInfo(): void;
};

function RegistrationConnectionPopovers({
  revisionState,
  hoveredEdge,
  hoverPosition,
  clickedEdge,
  clickPosition,
  unsetClickInfo,
}: RegistrationConnectionPopoversProps): JSX.Element | null {
  const { thresholdSet } = useThresholdSetContext();
  const [detailsEdge, setDetailsEdge] =
    useState<RegistrationEdgeRevision | null>(null);

  const hoverAnchor = useVirtualElement(hoverPosition);
  const hoveredSource = useAppSelector(
    hoveredEdge
      ? selectRevisionEntityScan(hoveredEdge.sourceId)
      : () => undefined,
  );
  const hoveredTarget = useAppSelector(
    hoveredEdge
      ? selectRevisionEntityScan(hoveredEdge.targetId)
      : () => undefined,
  );

  const clickAnchor = useVirtualElement(clickPosition);
  const clickedSource = useAppSelector(
    clickedEdge
      ? selectRevisionEntityScan(clickedEdge.sourceId)
      : () => undefined,
  );
  const clickedTarget = useAppSelector(
    clickedEdge
      ? selectRevisionEntityScan(clickedEdge.targetId)
      : () => undefined,
  );

  const edgeData = clickedEdge?.data;
  const registrationDetails = useMemo(
    () =>
      edgeData?.metrics
        ? new RegistrationDetails(
            edgeData.metrics.overlap,
            thresholdSet,
            edgeData.metrics.rlyHistogram,
          )
        : undefined,
    [edgeData?.metrics, thresholdSet],
  );
  const connectionOverallQuality = registrationDetails
    ? registrationDetails.registrationQuality
    : QualityStatus.UNKNOWN;
  if (detailsEdge) {
    return (
      <ConnectionQualityDialog
        registrationEdge={detailsEdge}
        isDialogOpen
        onDialogClose={() => {
          unsetClickInfo();
          setDetailsEdge(null);
        }}
      />
    );
  }

  if (clickedEdge) {
    return (
      <QualityPopover
        aria-label="simple connection quality report"
        scope={QualityScope.CONNECTION}
        quality={connectionOverallQuality}
        registrationDetails={registrationDetails}
        anchorEl={clickAnchor}
        isOpen
        placement="bottom"
        onDetailsButtonClick={() => {
          unsetClickInfo();
          setDetailsEdge(clickedEdge);
          Analytics.track(EventType.showDetailedConnectionQualityReport);
        }}
        closePopover={() => {
          unsetClickInfo();
        }}
        connectionScans={{
          sourceScanName: clickedSource?.name ?? "",
          targetScanName: clickedTarget?.name ?? "",
        }}
        revisionStatus={revisionState}
        registrationEdgeType={clickedEdge.type}
      />
    );
  }

  if (hoveredEdge) {
    return (
      <FaroPopover
        title="registration connection"
        placement="bottom"
        anchorEl={hoverAnchor}
        open
        showCloseButton={false}
        dark
        sx={{ minWidth: "320px", pointerEvents: "none" }}
      >
        <ConnectionScanNames
          sourceScanName={hoveredSource?.name ?? ""}
          targetScanName={hoveredTarget?.name ?? ""}
        />
      </FaroPopover>
    );
  }
  return null;
}

function useVirtualElement(positionScreen: Vector2): VirtualElement {
  return useMemo<VirtualElement>(
    () => ({
      getBoundingClientRect: () => ({
        top: positionScreen.y,
        left: positionScreen.x,
        right: positionScreen.x,
        bottom: positionScreen.y,
        width: 0,
        height: 0,
        x: positionScreen.x,
        y: positionScreen.y,
        toJSON: () => {},
      }),
    }),
    [positionScreen],
  );
}

/**
 *
 * @param event the event to check
 * @returns `true`, if the event also affects a capture marker, in addition to the connection lines.
 *  In this case, the capture marker events have priority and the connection event should be ignored.
 */
function eventAffectsMarkers(event: ThreeEvent<MouseEvent>): boolean {
  return event.intersections.some(
    (intersection) => intersection.object instanceof MapPlaceholders,
  );
}
